import React, { useState } from 'react';
import { Box, Typography, Chip, IconButton, Tooltip } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import EditIcon from '@mui/icons-material/Edit';
import AdjustIcon from '@mui/icons-material/Adjust';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';
import GlassCard from '../shared/GlassCard';
import { colors } from '../../styles/colors';

const getScoreColor = (score) => {
  if (score >= 80) return colors.success;
  if (score >= 65) return colors.primary;
  if (score >= 50) return colors.warning;
  return colors.danger;
};

const getProgressFill = (progress) => {
  if (progress > 75) return colors.success;
  if (progress >= 40) return '#2A5F8F';
  return colors.warning;
};

const tierStyles = {
  premium: { bgcolor: colors.infoBg, color: colors.info },
  pro: { bgcolor: colors.primaryBg, color: colors.primary },
  essentiel: { bgcolor: colors.neutralBg, color: colors.textMed },
};

const pulseKeyframes = {
  '@keyframes pulse': {
    '0%': { opacity: 1, transform: 'scale(1)' },
    '50%': { opacity: 0.5, transform: 'scale(1.4)' },
    '100%': { opacity: 1, transform: 'scale(1)' },
  },
};

const BrandPerformanceTable = ({ brands = [], onBrandClick, accentColor }) => {
  return (
    <GlassCard sx={{ p: '28px', ...pulseKeyframes }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5 }}>
        <Typography
          sx={{
            fontFamily: "'Playfair Display', Georgia, serif",
            fontSize: '1.2rem',
            fontWeight: 700,
            color: colors.charcoal,
          }}
        >
          Performance du portefeuille
        </Typography>
      </Box>

      <Box sx={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              {['Marque', 'Score', 'Evolution', 'Objectifs', 'Forfait', 'Actions', 'Actions rapides'].map((col, i) => (
                <th
                  key={col}
                  style={{
                    fontFamily: "'DM Sans', sans-serif",
                    fontSize: '0.72rem',
                    color: colors.textMed,
                    letterSpacing: '0.12em',
                    textTransform: 'uppercase',
                    fontWeight: 600,
                    padding: '12px 12px',
                    borderBottom: `1px solid ${colors.borderLight}`,
                    textAlign: 'left',
                    whiteSpace: 'nowrap',
                    // Hide Objectifs (index 3) and Actions count (index 5) on xs
                    ...(i === 3 || i === 5 ? { className: 'hide-xs' } : {}),
                  }}
                  className={i === 3 || i === 5 ? 'hide-xs' : undefined}
                >
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {brands.map((brand, rowIndex) => (
              <BrandRow
                key={brand.id}
                brand={brand}
                isLast={rowIndex === brands.length - 1}
                onBrandClick={onBrandClick}
                accentColor={accentColor}
              />
            ))}
          </tbody>
        </table>
      </Box>

      {/* Responsive style: hide columns on xs screens */}
      <style>{`
        @media (max-width: 600px) {
          .hide-xs { display: none !important; }
        }
      `}</style>
    </GlassCard>
  );
};

const BrandRow = ({ brand, isLast, onBrandClick, accentColor }) => {
  const navigate = useNavigate();
  const [hovered, setHovered] = useState(false);
  const scoreColor = getScoreColor(brand.score_global);
  const tier = brand.tier || 'essentiel';
  const tierStyle = tierStyles[tier] || tierStyles.essentiel;

  const tdStyle = {
    padding: '14px 12px',
    borderBottom: isLast ? 'none' : `1px solid ${colors.borderLight}`,
    fontSize: '0.88rem',
    color: colors.text,
    verticalAlign: 'middle',
  };

  return (
    <tr
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: hovered ? (accentColor ? `${accentColor}08` : 'rgba(27,58,92,0.03)') : 'transparent',
        transition: 'background 0.2s',
        cursor: 'pointer',
      }}
    >
      {/* Marque */}
      <td style={tdStyle} onClick={() => onBrandClick(brand.id)}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                bgcolor: scoreColor,
                flexShrink: 0,
              }}
            />
            {brand.crises_active > 0 && (
              <Box
                sx={{
                  position: 'absolute',
                  top: -2,
                  right: -2,
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  bgcolor: colors.danger,
                  animation: 'pulse 2s infinite',
                }}
              />
            )}
          </Box>
          <Box>
            <Typography
              sx={{
                fontFamily: "'DM Sans', sans-serif",
                fontWeight: 600,
                color: colors.charcoal,
                fontSize: '0.8rem',
                lineHeight: 1.3,
              }}
            >
              {brand.name}
            </Typography>
            <Typography
              sx={{
                fontFamily: "'DM Sans', sans-serif",
                fontSize: '0.68rem',
                color: colors.textLight,
                lineHeight: 1.3,
              }}
            >
              {brand.sector}
            </Typography>
          </Box>
        </Box>
      </td>

      {/* Score */}
      <td style={tdStyle}>
        <Typography
          sx={{
            fontFamily: "'Playfair Display', Georgia, serif",
            fontSize: '0.95rem',
            fontWeight: 500,
            color: scoreColor,
          }}
        >
          {brand.score_global != null ? brand.score_global.toFixed(1) : '—'}
        </Typography>
      </td>

      {/* Evolution */}
      <td style={tdStyle}>
        <EvolutionChip delta={brand.score_delta} />
      </td>

      {/* Objectifs */}
      <td style={tdStyle} className="hide-xs">
        <Box>
          <Box
            sx={{
              width: 60,
              height: 4,
              borderRadius: '2px',
              bgcolor: colors.borderLight,
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                width: `${Math.min(brand.objectives_progress || 0, 100)}%`,
                height: '100%',
                borderRadius: '2px',
                bgcolor: getProgressFill(brand.objectives_progress || 0),
                transition: 'width 0.4s ease',
              }}
            />
          </Box>
          <Typography
            sx={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '0.68rem',
              color: colors.textMed,
              mt: 0.3,
            }}
          >
            {brand.objectives_progress != null ? `${brand.objectives_progress}%` : '—'}
          </Typography>
        </Box>
      </td>

      {/* Forfait */}
      <td style={tdStyle}>
        <Chip
          label={tier.charAt(0).toUpperCase() + tier.slice(1)}
          size="small"
          sx={{
            fontFamily: "'DM Sans', sans-serif",
            fontSize: '0.68rem',
            fontWeight: 600,
            color: tierStyle.color,
            bgcolor: tierStyle.bgcolor,
            borderRadius: '100px',
            height: 22,
          }}
        />
      </td>

      {/* Actions pending */}
      <td style={tdStyle} className="hide-xs">
        {brand.actions_pending > 0 ? (
          <Chip
            label={brand.actions_pending}
            size="small"
            sx={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '0.72rem',
              fontWeight: 600,
              bgcolor: colors.primaryBg,
              color: colors.primary,
              height: 22,
              minWidth: 28,
            }}
          />
        ) : (
          <Typography sx={{ color: colors.textLight, fontSize: '0.8rem' }}>
            —
          </Typography>
        )}
      </td>

      {/* Actions rapides — redirect to Settings tabs */}
      <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>
        <Box sx={{ display: 'flex', gap: 0.25 }}>
          <Tooltip title="Mots-cles (Parametres)" arrow>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                navigate('/settings?tab=0&brand=' + brand.id);
              }}
              sx={{
                color: colors.textLight,
                '&:hover': { color: accentColor || colors.primary },
                transition: 'color 0.2s',
              }}
            >
              <EditIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Objectifs (Parametres)" arrow>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                navigate('/settings?tab=2&brand=' + brand.id);
              }}
              sx={{
                color: colors.textLight,
                '&:hover': { color: accentColor || colors.primary },
                transition: 'color 0.2s',
              }}
            >
              <AdjustIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Forfait (Parametres)" arrow>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                navigate('/settings?tab=4&brand=' + brand.id);
              }}
              sx={{
                color: colors.textLight,
                '&:hover': { color: accentColor || colors.primary },
                transition: 'color 0.2s',
              }}
            >
              <OpenInNewIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </td>
    </tr>
  );
};

const EvolutionChip = ({ delta }) => {
  if (delta == null || delta === 0) {
    return (
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          bgcolor: 'rgba(196,146,10,0.1)',
          color: colors.warning,
          borderRadius: '100px',
          px: 1,
          py: 0.25,
          fontSize: '0.72rem',
          fontFamily: "'DM Sans', sans-serif",
          fontWeight: 600,
        }}
      >
        <TrendingFlatIcon sx={{ fontSize: 14 }} />
        =
      </Box>
    );
  }

  const isPositive = delta > 0;

  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        bgcolor: isPositive ? 'rgba(46,158,107,0.1)' : 'rgba(180,64,64,0.1)',
        color: isPositive ? colors.success : colors.danger,
        borderRadius: '100px',
        px: 1,
        py: 0.25,
        fontSize: '0.72rem',
        fontFamily: "'DM Sans', sans-serif",
        fontWeight: 600,
      }}
    >
      {isPositive ? (
        <TrendingUpIcon sx={{ fontSize: 14 }} />
      ) : (
        <TrendingDownIcon sx={{ fontSize: 14 }} />
      )}
      {isPositive ? `+${delta.toFixed(1)}` : delta.toFixed(1)}
    </Box>
  );
};

export default BrandPerformanceTable;
